/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DispatchLocationFormView.tsx
 🗓️ Created: 2025-12-04
 🧩 Description:
    Form view for creating and editing Dispatch Locations (RAY BAUM's Act)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { Toggle } from '../../components/ui/Toggle';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import {
  fetchDispatchLocationById,
  createDispatchLocation,
  updateDispatchLocation,
} from './api/dispatchLocationApi';
import type { DispatchLocationFormState } from './types';

const SELECT_CLASS = 'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CL', name: 'Chile' },
  { code: 'PE', name: 'Peru' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'PA', name: 'Panama' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'HN', name: 'Honduras' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'CU', name: 'Cuba' },
  { code: 'JM', name: 'Jamaica' },
];

const UNIT_TYPES = [
  'Apartment', 'Basement', 'Building', 'Department',
  'Floor', 'Office', 'Penthouse', 'Suite', 'Trailer', 'Unit',
];

const defaultFormData: DispatchLocationFormState = {
  locationName: '',
  locationCode: '',
  assignmentType: 'global',
  extensionId: null,
  deviceMac: '',
  extension: '',
  siteId: null,
  streetNumber: '',
  streetName: '',
  streetSuffix: '',
  unitType: '',
  unitNumber: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  buildingName: '',
  floor: '',
  room: '',
  wing: '',
  latitude: '',
  longitude: '',
  elin: '',
  formattedAddress: '',
  dispatchLocation: '',
  sipHeaders: {},
  pidfLoXml: '',
  contactName: '',
  contactPhone: '',
  specialInstructions: '',
  validated: false,
  validatedAt: '',
  validatedBy: '',
  settings: {},
  enabled: true,
  notes: '',
};

export default function DispatchLocationFormView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<DispatchLocationFormState>(defaultFormData);
  const [originalData, setOriginalData] = useState<DispatchLocationFormState>(defaultFormData);
  const [hasChanges, setHasChanges] = useState(false);

  const mode = id && id !== 'new' ? 'edit' : 'create';
  const isEditMode = mode === 'edit';

  useEffect(() => {
    if (isEditMode) loadDispatchLocation();
  }, [id, isEditMode]);

  const loadDispatchLocation = async () => {
    try {
      setLoading(true);
      const data = await fetchDispatchLocationById(id!);
      const s: DispatchLocationFormState = {
        locationName: data.locationName,
        locationCode: data.locationCode || '',
        assignmentType: data.assignmentType,
        extensionId: data.extensionId,
        deviceMac: data.deviceMac || '',
        extension: data.extension || '',
        siteId: data.siteId || null,
        streetNumber: data.streetNumber || '',
        streetName: data.streetName || '',
        streetSuffix: data.streetSuffix || '',
        unitType: data.unitType || '',
        unitNumber: data.unitNumber || '',
        city: data.city || '',
        state: data.state || '',
        postalCode: data.postalCode || '',
        country: data.country || 'US',
        buildingName: data.buildingName || '',
        floor: data.floor || '',
        room: data.room || '',
        wing: data.wing || '',
        latitude: data.latitude || '',
        longitude: data.longitude || '',
        elin: data.elin || '',
        formattedAddress: data.formattedAddress || '',
        dispatchLocation: data.dispatchLocation || '',
        sipHeaders: data.sipHeaders || {},
        pidfLoXml: data.pidfLoXml || '',
        contactName: data.contactName || '',
        contactPhone: data.contactPhone || '',
        specialInstructions: data.specialInstructions || '',
        validated: data.validated || false,
        validatedAt: data.validatedAt || '',
        validatedBy: data.validatedBy || '',
        settings: data.settings || {},
        enabled: data.enabled,
        notes: data.notes || '',
      };
      setFormData(s);
      setOriginalData(s);
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('dispatchLocations:messages.loadError');
      showError(msg);
      navigate('/pbx/emergency/locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHasChanges(JSON.stringify(formData) !== JSON.stringify(originalData));
  }, [formData, originalData]);

  const handleSave = useCallback(async () => {
    try {
      setLoading(true);
      let savedData;
      if (isEditMode) {
        savedData = await updateDispatchLocation(id!, formData);
        showSuccess(t('dispatchLocations:messages.updateSuccess'));
      } else {
        savedData = await createDispatchLocation(formData);
        showSuccess(t('dispatchLocations:messages.createSuccess'));
      }
      setOriginalData({ ...formData });
      setHasChanges(false);
      return savedData;
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('dispatchLocations:messages.saveError');
      showError(msg);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [formData, isEditMode, id, showSuccess, showError, t]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSave,
    listPath: '/pbx/emergency/locations',
    newPath: '/pbx/emergency/locations/new',
    mode,
    getEditPath: (s) => `/pbx/emergency/locations/${s.id}`,
    storageKey: 'dispatchLocations:saveAction',
  });

  const handleCancel = useCallback(() => {
    setFormData({ ...originalData });
    setHasChanges(false);
  }, [originalData]);

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<MapPin className="h-5 w-5" />}
        title={isEditMode
          ? `${t('dispatchLocations:editTitle')} - ${formData.locationName || ''}`
          : t('dispatchLocations:createTitle')}
        backButtonText={t('dispatchLocations:actions.backToList')}
        onBack={() => navigate('/pbx/emergency/locations')}
      />

      <div className="space-y-4 mt-8">
        <FormBox title={t('dispatchLocations:sections.general', 'General')} icon={<MapPin className="h-5 w-5" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">

            {/* Row 1: Location Name | Country */}
            <SimpleTooltip content={t('dispatchLocations:tooltips.locationName')} title={t('dispatchLocations:fields.locationName', 'Location Name')} position="top" maxWidth="300px">
              <Label htmlFor="locationName" required className="text-left">
                {t('dispatchLocations:fields.locationName', 'Location Name')}
              </Label>
            </SimpleTooltip>
            <Input
              id="locationName"
              value={formData.locationName}
              onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
              placeholder="Main Office"
            />

            <SimpleTooltip content="The country where this emergency location is physically located" title={t('dispatchLocations:fields.country', 'Country')} position="top" maxWidth="300px">
              <Label htmlFor="country" required className="text-left">
                {t('dispatchLocations:fields.country', 'Country')}
              </Label>
            </SimpleTooltip>
            <select id="country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className={SELECT_CLASS}>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>

            {/* Row 2: Street Address | State */}
            <SimpleTooltip content="Physical street address for this emergency dispatch location" title={t('dispatchLocations:fields.streetAddress', 'Street Address')} position="top" maxWidth="300px">
              <Label htmlFor="streetNumber" required className="text-left">
                {t('dispatchLocations:fields.streetAddress', 'Street Address')}
              </Label>
            </SimpleTooltip>
            <div className="flex gap-2">
              <Input
                id="streetNumber"
                value={formData.streetNumber}
                onChange={(e) => setFormData({ ...formData, streetNumber: e.target.value })}
                placeholder="123"
                className="w-1/3"
              />
              <Input
                id="streetName"
                value={formData.streetName}
                onChange={(e) => setFormData({ ...formData, streetName: e.target.value })}
                placeholder="Main St"
                className="w-2/3"
              />
            </div>

            <SimpleTooltip content="State or province" title={t('dispatchLocations:fields.state', 'State')} position="top" maxWidth="300px">
              <Label htmlFor="state" required className="text-left">
                {t('dispatchLocations:fields.state', 'State')}
              </Label>
            </SimpleTooltip>
            <Input
              id="state"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              placeholder="FL"
            />

            {/* Row 3: Address Type | City */}
            <SimpleTooltip content="Unit type and number (e.g. Suite 200, Floor 3)" title={t('dispatchLocations:fields.addressType', 'Address Type')} position="top" maxWidth="300px">
              <Label htmlFor="unitType" className="text-left">
                {t('dispatchLocations:fields.addressType', 'Address Type')}
              </Label>
            </SimpleTooltip>
            <div className="flex gap-2">
              <select id="unitType" value={formData.unitType} onChange={(e) => setFormData({ ...formData, unitType: e.target.value })} className={`${SELECT_CLASS} w-1/2`}>
                <option value="">-- Select Type --</option>
                {UNIT_TYPES.map(ut => <option key={ut} value={ut}>{ut}</option>)}
              </select>
              <Input
                id="unitNumber"
                value={formData.unitNumber}
                onChange={(e) => setFormData({ ...formData, unitNumber: e.target.value })}
                placeholder="Unit #"
                className="w-1/2"
              />
            </div>

            <SimpleTooltip content="City where this location is physically located" title={t('dispatchLocations:fields.city', 'City')} position="top" maxWidth="300px">
              <Label htmlFor="city" required className="text-left">
                {t('dispatchLocations:fields.city', 'City')}
              </Label>
            </SimpleTooltip>
            <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Miami" />

            {/* Row 4: Caller ID (ELIN) | Postal/ZIP Code */}
            <SimpleTooltip content="Emergency Location Identification Number (ELIN) — phone number reported to the PSAP (911) to identify this location" title={t('dispatchLocations:fields.callerId', 'Caller ID')} position="top" maxWidth="300px">
              <Label htmlFor="elin" className="text-left">
                {t('dispatchLocations:fields.callerId', 'Caller ID')}
              </Label>
            </SimpleTooltip>
            <Input id="elin" value={formData.elin} onChange={(e) => setFormData({ ...formData, elin: e.target.value })} placeholder="+13055551234" className="font-mono" />

            <SimpleTooltip content="ZIP or postal code for this location" title={t('dispatchLocations:fields.postalCode', 'Postal/ZIP Code')} position="top" maxWidth="300px">
              <Label htmlFor="postalCode" required className="text-left">
                {t('dispatchLocations:fields.postalCode', 'Postal/ZIP Code')}
              </Label>
            </SimpleTooltip>
            <Input id="postalCode" value={formData.postalCode} onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })} placeholder="33101" />

            {/* Row 5: Default | (empty) */}
            <SimpleTooltip content="Set this as the default dispatch location for the domain" title={t('dispatchLocations:fields.default', 'Default')} position="top" maxWidth="300px">
              <Label htmlFor="enabled" className="text-left">
                {t('dispatchLocations:fields.default', 'Default')}
              </Label>
            </SimpleTooltip>
            <Toggle
              id="enabled"
              value={formData.enabled}
              onChange={(value) => setFormData({ ...formData, enabled: value })}
            />

          </div>
        </FormBox>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        isEditMode={isEditMode}
        isSaving={loading}
        cancelText={t('common:cancel')}
        useSaveDropdown={true}
        saveDropdownStorageKey="dispatchLocations:saveAction"
        saveDisabled={loading}
      />
    </div>
  );
}
